import React, { useEffect, useMemo } from "react";
import { t } from "i18next";
import { useParams, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import C from "Constants";
import { UTILS } from "utils";
import { OrderAction } from "store/actions";
import { WhatsappIcon, CheckIcon, BubbleIcon, PhoneIcon } from "icons";
import { TranslationText } from "components/TextTranslationContainer";
import { PageHeaderV2 } from "components/PageHeader";
import { ErrorComponent } from "components";
import { Spinner } from "components";
import TrackOrderStatus from "./TrackOrderStatus";

export const StepTracking = ({ orderStatus, storeName }) => {
  const steps = [
    { key: "ACCEPTED", label: "Accepted" },
    { key: "PREPARING", label: "Preparing" },
    { key: "COMPLETE", label: "Served" },
  ];

  const getStepStatus = (status) => {
    switch (status) {
      case "ACCEPTED":
        return 0;
      case "COOKING":
        return 1;
      case "DELIVERED":
        return 2;
      default:
        return -1;
    }
  };

  const currentStep = getStepStatus(orderStatus);

  const name = storeName || "Marketplace";

  const statusMessages = {
    PENDING: "is reviewing your order.",
    ACCEPTED: "has accepted your order.",
    PREPARING: "is currently preparing your order.",
    COMPLETE: "Your order has been served. Enjoy!",
  };

  return (
    <div className="w-full flex flex-col gap-2">
      <div className="flex items-center justify-between w-full max-w-md mx-auto">
        {steps.map((step, idx) => (
          <React.Fragment key={step.key}>
            {/* Step circle */}
            <div className="flex flex-col items-center">
              {idx < currentStep || idx === currentStep ? (
                <CheckIcon className="primary-svg-rect-fill" />
              ) : (
                <BubbleIcon className="primary-svg-rect-fill" />
              )}
              <TranslationText
                className="mt-1 text-12x font-normal text-dark-blue-900"
                text={step?.label?.toLowerCase()}
              />
            </div>

            {/* Connector line */}
            {idx < steps.length - 1 && (
              <div
                className={`h-1 flex-1 mx-2 
                ${idx < currentStep ? "bg-brand-base-primary" : "bg-gray-300"}
              `}
                style={{
                  marginBottom: "25px",
                }}
              />
            )}
          </React.Fragment>
        ))}
      </div>
      <TranslationText
        text={statusMessages[orderStatus]}
        contentProps={{ storeName: name }}
        className="text-14x font-normal text-dark-blue-900"
      />
    </div>
  );
};

const TrackOrder = ({ goBack = () => {}, goToLogin = () => {}, goToSettings = () => {}, children }) => {
  const dispatch = useDispatch();
  const params = useParams();
  const history = useHistory();

  const { isLoading } = useSelector((state) => state?.loader);
  const { loading } = useSelector((state) => state?.brand);
  const { orderStatus, orderInfo, error } = useSelector((state) => state.order);
  const { selectedStore } = useSelector((state) => state?.store);
  const { DomainType } = useSelector((state) => state.AppReducer);
  const { brandInfo } = useSelector((state) => state.brandStorageReducer);


  useEffect(() => {
    if (params?.orderId) {
      if (params?.domainName && DomainType === C.TEMPORARAY_DOMAIN) {
        const orderId = params.orderId;
        const obj = {
          orderId,
          history,
        };
        dispatch(OrderAction.getOrderStatus(obj));
      } else {
        const orderId = params.orderId;
        const obj = {
          orderId,
          history,
        };
        dispatch(OrderAction.getOrderStatus(obj));
      }
    }
  }, []);

  const getOrderStatus = (orderInfo) => {
    if (orderInfo) {
      const status = UTILS.getOrderStatus(orderInfo);
      if (status)
        return (
          <StepTracking
            orderStatus={orderInfo?.orderStatus}
            storeName={selectedStore?.baseLanguage?.name}
          />
        );
      else
        return (
          <TrackOrderStatus orderStatus={orderStatus} orderInfo={orderInfo} />
        );
    }
  };

  const orderDate = useMemo(() => {
    const date = orderInfo?.orderPlacedTime
    if (typeof date === "number" && date.toString().length === 10) {
      return moment.unix(date).format("DD MMM YYYY HH:mm");
    }
    // Fallback to regular moment parsing
    return moment(date).format("DD MMM YYYY HH:mm");
  }, [orderInfo]);

  const options = [];

  if (selectedStore?.whatsAppContact || orderInfo?.storeInfo?.whatsAppContact) {
    options.push({
      icon: <WhatsappIcon />,
      value:
        selectedStore?.whatsAppContact || orderInfo?.storeInfo?.whatsAppContact,
      label: "Whatsapp",
      link: `https://wa.me/${
        selectedStore?.whatsAppContact || orderInfo?.storeInfo?.whatsAppContact
      }`,
      hoverClass: "hover:bg-brand-base-primary",
    });
  }

  if (selectedStore?.mobileNumber || orderInfo?.storeInfo?.mobileNumber) {
    options.push({
      icon: (
        <PhoneIcon />
      ),
      value: selectedStore?.mobileNumber || orderInfo?.storeInfo?.mobileNumber,
      label: "Phone",
      link: `tel:${
        selectedStore?.mobileNumber || orderInfo?.storeInfo?.mobileNumber
      }`,
       hoverClass:"hover:bg-brand-base-primary hover:text-white"
    });
  }

  if (isLoading || loading) return <Spinner />;

  return (
    <div className="relative flex flex-col gap-8 items-center p-5">
      <PageHeaderV2
        pageTitle={"Track Your Order"}
        onGoBack={goBack}
        authClick={goToLogin}
        userClick={goToSettings}
        containerStyleProps={{
          className: "relative",
          style: {
            padding: "0",
          },
        }}
        headerStyleProps={{
          style: {
            padding: "0",
          },
        }}

        // hideAuth={true}
      />
      <div className="w-full flex flex-col gap-6">
        <div className="flex justify-between items-center w-full">
          <h3 className="text-dark-blue-700 text-14x">
            {t("Order Date")}
            <span> : </span>
            <span className="font-bold">
              {orderDate}
            </span>
          </h3>
          <h3 className="text-dark-blue-700 text-14x">
            {t("Order ID")}
            <span> : </span>
            <span className="font-bold">{orderInfo?.orderNumber}</span>
          </h3>
        </div>

        {error && <ErrorComponent error={error} />}
        {getOrderStatus(orderInfo)}
        <hr className="my-2 border-gray-200" />
        <div className="flex justify-between gap-2">
          <div className="flex gap-2 items-center">
            <img
              src={brandInfo?.setting?.brandLogoFirst || C.PLACEHODERIMAGE}
              alt={brandInfo?.brand?.brandName || "Marketplace"}
              className="w-10 h-10 rounded-8x object-cover"
            />
            <h2 className="m-0 text-14x font-medium text-natural-900 font-poppins">
              {t(
                selectedStore?.baseLanguage?.name ??
                orderInfo?.storeInfo?.baseLanguage?.name ??
                  "Marketplace"
              )}
            </h2>
          </div>
          <div className="flex gap-2 items-center">
            {options.map((option, index) => (
              <a
                href={option?.link}
                key={index}
                className={` w-11 h-11 rounded-full bg-svg-stroke flex items-center justify-center ${option?.hoverClass}`}
              >
                {option?.icon}
              </a>
            ))}
          </div>
        </div>
      </div>
      {children ? children : null}
    </div>
  );
};

export default TrackOrder;
